import { fetchJSON as fetchAPI } from './client';

const RUNTIME_BASE = import.meta.env.VITE_RUNTIME_API ?? 'http://localhost:8159';

export type ModuleCompliance = {
  module_id: string;
  compliant: boolean;
  plugin_registered: boolean;
  platform_wired: boolean;
  ownership_valid: boolean;
  migrations_ok: boolean;
  api_governance_ok: boolean;
  test_coverage_pct: number;
  violations?: string[];
};

export type PluginRegistration = {
  module_id: string;
  plugin_id: string;
  version: string;
  state: string;
  capabilities: string[];
  extension_points: string[];
  health_ok: boolean;
  telemetry_enabled: boolean;
  hot_reload: boolean;
};

export type MigrationRecord = {
  service: string;
  version: string;
  filename: string;
  has_up: boolean;
  has_down: boolean;
  has_verify: boolean;
  status: string;
};

export type ComplianceGap = {
  id: string;
  severity: string;
  description: string;
  remediation: string;
};

export type ComplianceReport = {
  generated_at: string;
  overall_score: number;
  certification_ready: boolean;
  blk004_test_coverage: number;
  blk005_plugin_integrated: boolean;
  warehouse_ownership_resolved: boolean;
  migration_rollback_ok: boolean;
  platform_wiring_ok: boolean;
  api_governance_ok: boolean;
  modules: ModuleCompliance[];
  plugins: PluginRegistration[];
  migrations: MigrationRecord[];
  gaps?: ComplianceGap[];
};

export type APIGovernanceStandards = {
  pagination: boolean;
  filtering: boolean;
  sorting: boolean;
  rate_limiting: boolean;
  error_model: boolean;
  problem_details: boolean;
  openapi: boolean;
  api_versioning: boolean;
  request_validation: boolean;
  response_validation: boolean;
  backward_compatibility: boolean;
};

function fetchRuntime<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(RUNTIME_BASE, path, init);
}

export const complianceApi = {
  report: () => fetchRuntime<ComplianceReport>('/api/v1/compliance'),

  modules: () => fetchRuntime<{ modules: ModuleCompliance[]; count: number }>('/api/v1/compliance/modules'),

  platform: () => fetchRuntime<{ platform_matrix: unknown[]; wiring_ok: boolean }>('/api/v1/compliance/platform'),

  plugins: () => fetchRuntime<{ plugins: PluginRegistration[]; count: number }>('/api/v1/compliance/plugins'),

  migrations: () => fetchRuntime<{ migrations: MigrationRecord[]; rollback_ok: boolean }>('/api/v1/compliance/migrations'),

  apiGovernance: () => fetchRuntime<{ standards: APIGovernanceStandards; compliant: boolean }>('/api/v1/compliance/api-governance'),

  validate: (body?: { module_id?: string; scope?: string }) =>
    fetchRuntime<{ valid: boolean; violations?: string[]; module?: ModuleCompliance }>(
      '/api/v1/compliance/validate',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body ?? {}) },
    ),
};
